import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import fgLogo from '../../imgs/fg_logo.png';
/**
 * 从登录 Presenter 导入真实登录接口
 * login: 用户登录认证（接口文档 #1）
 * forgetPasswordVerifyEmail: 忘记密码邮件
 *
 * 注：2026-10-16 起，平台仅支持"富工作扫码登录自动注册"，
 * 不再提供邮箱自助注册入口，故移除 registerVerifyCode / registerAdd。
 */
import { login, forgetPasswordVerifyEmail } from './LoginPresenter';
import { getItem } from '../../utils/localStorageUtil';
import { LOCAL_STORAGE_KEYS } from '../../const/localStorageConst';
import { savePasswordLoginSession } from '../../utils/authSession';
import { consumeExpiredNotice } from '../../utils/portalSession';
import FullworkQrLogin from '../../components/FullworkQrLogin';
import './Login.css';

/**
 * 根据本地登录状态计算登录页的目标跳转地址
 * - 已登录管理员：跳转管理端首页
 * - 已登录普通用户：跳转普通用户首页
 * - 未登录：返回 null，停留登录页
 */
const getLoggedInRedirectPath = () => {
  const token = getItem(LOCAL_STORAGE_KEYS.TOKEN) || window.localStorage?.getItem(LOCAL_STORAGE_KEYS.TOKEN) || window.sessionStorage?.getItem(LOCAL_STORAGE_KEYS.TOKEN);
  if (!token) return null;

  const userInfo = getItem(LOCAL_STORAGE_KEYS.USER_INFO, {});
  const storedIsAdmin = getItem(LOCAL_STORAGE_KEYS.IS_ADMIN);
  const isAdmin = storedIsAdmin === true || storedIsAdmin === 'true' || userInfo?.is_admin === true || userInfo?.is_admin === 'true';

  return isAdmin ? '/admin-dashboard' : '/user-dashboard';
};

const Login = () => {
  const navigate = useNavigate();
  const loggedInRedirectPath = getLoggedInRedirectPath();

  useEffect(() => {
    if (consumeExpiredNotice()) message.warning('登录状态已过期，请重新登录');
  }, []);

  useLayoutEffect(() => {
    if (loggedInRedirectPath) {
      navigate(loggedInRedirectPath, { replace: true });
    }
  }, [loggedInRedirectPath, navigate]);

  // 登录表单
  const [account, setAccount] = useState('');
  const [password, setPassword] = useState('');
  const [rememberPassword, setRememberPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const [loading, setLoading] = useState(false);
  // 忘记密码模式
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotCooldown, setForgotCooldown] = useState(0);
  const forgotCooldownRef = useRef(null);

  const accountText = String(account || '');
  const passwordText = String(password || '');
  const canSubmit = Boolean(accountText.trim() && passwordText.trim());
  const isSubmitDisabled = !canSubmit || loading || isLeaving;

  // 组件卸载时清理倒计时
  useEffect(() => {
    return () => {
      if (forgotCooldownRef.current) {
        window.clearInterval(forgotCooldownRef.current);
      }
    };
  }, []);

  // 简单的邮箱格式校验
  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || ''));

  // 忘记密码 — 发送邮箱验证
  const handleForgotSubmit = async (event) => {
    event.preventDefault();

    const emailText = String(forgotEmail || '').trim();
    if (!emailText) {
      message.warning('请输入邮箱地址');
      return;
    }
    if (!isValidEmail(emailText)) {
      message.warning('请输入有效的邮箱地址');
      return;
    }
    if (forgotCooldown > 0 || loading) {
      return;
    }

    setLoading(true);
    try {
      const res = await forgetPasswordVerifyEmail({ email_account: emailText });
      if (res && res.status === 200) {
        message.success('验证邮件已发送，请查收邮箱并点击链接重置密码');
        setForgotCooldown(30);
        forgotCooldownRef.current = window.setInterval(() => {
          setForgotCooldown((prev) => {
            if (prev <= 1) {
              window.clearInterval(forgotCooldownRef.current);
              forgotCooldownRef.current = null;
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        message.error(res?.msg || '发送失败，请稍后重试');
      }
    } catch (err) {
      message.error('请求失败，请检查网络后重试');
    } finally {
      setLoading(false);
    }
  };

  // 从忘记密码模式返回登录模式
  const handleBackToLogin = () => {
    setForgotMode(false);
    setForgotEmail('');
    setForgotCooldown(0);
    if (forgotCooldownRef.current) {
      window.clearInterval(forgotCooldownRef.current);
      forgotCooldownRef.current = null;
    }
  };

  // 记住密码：组件挂载时从 localStorage 读取上次保存的账号密码自动回填
  useEffect(() => {
    const savedUsername = getItem(LOCAL_STORAGE_KEYS.LOGIN_USERNAME);
    const savedPwd = getItem(LOCAL_STORAGE_KEYS.LOGIN_PWD);
    if (savedUsername) {
      setAccount(String(savedUsername));
      setRememberPassword(true);
    }
    if (savedPwd) {
      setPassword(String(savedPwd));
    }
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isSubmitDisabled) {
      return;
    }

    setLoading(true);

    try {
      const res = await login({ username: accountText.trim(), pwd: passwordText });

      if (res && res.status === 200) {
        const saveResult = savePasswordLoginSession(res, { username: accountText.trim(), pwd: passwordText });
        if (!saveResult.success) {
          setLoading(false);
          message.error(saveResult.msg);
          return;
        }

        setIsLeaving(true);
        navigate(saveResult.redirectPath);
      } else {
        setLoading(false);
        message.error(res?.msg || '登录失败');
      }
    } catch (error) {
      setLoading(false);
      message.error('登录请求失败，请检查网络后重试');
    }
  };

  if (loggedInRedirectPath) {
    return null;
  }

  return (
    <div className={`login-page${isLeaving ? ' login-page--leaving' : ''}`}>
      {/* 背景：奶油米白 + 两侧橄榄绿淡光晕 */}
      <div className="login-page__background" />
      <div className="login-page__aurora login-page__aurora--left" />
      <div className="login-page__aurora login-page__aurora--right" />

      {/* 中央单列卡片 */}
      <div className="login-page__content">
        <section className="login-card">
          {/* 品牌区 */}
          <div className="login-card__brand">
            <img src={fgLogo} alt="富小助" className="login-card__logo" />
            <div>
              <div className="login-card__brand-name">富小助平台</div>
              <div className="login-card__brand-tagline">连接你的专属 AI 助手</div>
            </div>
          </div>

          {/* 标题区 */}
          <div className="login-card__header">
            {forgotMode ? (
              <>
                <h2>找回密码</h2>
                <p>输入注册邮箱，我们将发送重置链接</p>
              </>
            ) : (
              <>
                <h2>欢迎回来</h2>
                <p>登录后继续使用你的 AI 助手能力中心</p>
              </>
            )}
          </div>

          {forgotMode ? (
            /* ===== 忘记密码模式 ===== */
            <form className="login-form" onSubmit={handleForgotSubmit}>
              <label className="login-form__field">
                <span>邮箱地址</span>
                <input
                  type="email"
                  value={forgotEmail}
                  onChange={(event) => setForgotEmail(event.target.value)}
                  placeholder="请输入注册邮箱"
                  autoComplete="email"
                />
              </label>

              <button
                type="submit"
                className="login-form__submit"
                disabled={loading || forgotCooldown > 0}
              >
                <span>
                  {loading
                    ? '发送中...'
                    : forgotCooldown > 0
                      ? `已发送 (${forgotCooldown}s)`
                      : '发送验证邮件'}
                </span>
                {loading && <span className="login-form__submit-loading" />}
              </button>

              <button
                type="button"
                className="login-form__back-link"
                onClick={handleBackToLogin}
              >
                ← 返回登录
              </button>
            </form>
          ) : (
            /* ===== 登录模式 ===== */
            <form className="login-form" onSubmit={handleSubmit}>
              <label className="login-form__field">
                <span>账号</span>
                <input
                  type="text"
                  value={account}
                  onChange={(event) => setAccount(event.target.value)}
                  placeholder="请输入邮箱"
                  autoComplete="username"
                />
              </label>

              <label className="login-form__field">
                <span>密码</span>
                <div className="login-form__password-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder="请输入登录密码"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="login-form__toggle"
                    onClick={() => setShowPassword((current) => !current)}
                  >
                    {showPassword ? '隐藏' : '显示'}
                  </button>
                </div>
              </label>

              <div className="login-form__meta">
                <label className="login-form__remember">
                  <input
                    type="checkbox"
                    checked={rememberPassword}
                    onChange={(event) => setRememberPassword(event.target.checked)}
                  />
                  <span>记住密码</span>
                </label>

                <div className="login-form__meta-right">
                  <button type="button" className="login-form__link" onClick={() => {
                    const savedUsername = getItem(LOCAL_STORAGE_KEYS.LOGIN_USERNAME);
                    setForgotEmail(savedUsername ? String(savedUsername) : '');
                    setForgotMode(true);
                  }}>
                    忘记密码？
                  </button>
                </div>
              </div>

              <button type="submit" className="login-form__submit" disabled={isSubmitDisabled}>
                <span>{isLeaving ? '进入中...' : '登录平台'}</span>
                {loading && !isLeaving && <span className="login-form__submit-loading" />}
              </button>

              <FullworkQrLogin />
            </form>
          )}
        </section>
      </div>
    </div>
  );
};

export default Login;
